import React, { useEffect, useRef, useState } from 'react';

const PRINCIPLES = [
  {
    icon: '🤝',
    title: 'Teamwork',
    desc: 'We work together, combining the strengths, knowledge, and skills of all partners to deliver timely, effective, and life-saving responses. Every member\'s contribution is valued, and shared responsibility ensures collective success.',
  },
  {
    icon: '✅',
    title: 'Accountability',
    desc: 'We take responsibility for our actions, decisions, and use of resources. We commit to ethical conduct and ensure that communities, partners, and stakeholders can trust that we act in their best interests.',
  },
  {
    icon: '🔍',
    title: 'Transparency',
    desc: 'We operate openly and honestly, sharing information, plans, and outcomes with partners, communities, and stakeholders. Transparency builds trust and strengthens collective decision-making.',
  },
  {
    icon: '🌐',
    title: 'Collaboration',
    desc: 'We actively engage with NGOs, government, and other stakeholders to jointly plan, learn, and implement solutions. Collaboration enables us to respond more efficiently and maximize our collective impact.',
  },
  {
    icon: '🙌',
    title: 'Cooperation',
    desc: 'We support and assist each other in achieving shared goals, respecting each partner\'s role and expertise. Cooperation ensures smoother coordination and more effective disaster response.',
  },
  {
    icon: '🔗',
    title: 'Networking',
    desc: 'We connect, exchange knowledge, and build partnerships across local, national, and international levels. Networking allows us to share resources, align technical capacities, and strengthen preparedness and response systems.',
  },
];

const EXEC_SEATS = [
  {
    icon: '🌐',
    seats: 3,
    type: 'International NGO Representatives',
    desc: 'Three seats reserved for international NGOs operating humanitarian and disaster management programs in Tanzania. Elected by international NGO members.',
    accent: 'from-blue-400 to-blue-600',
    cardHover: 'hover:border-blue-500/30',
    iconWrap: 'bg-blue-500/10 border-blue-500/20',
    seatColor: 'text-blue-400',
  },
  {
    icon: '🇹🇿',
    seats: 2,
    type: 'National NGO Representatives',
    desc: 'Two seats for Tanzanian-registered NGOs engaged in local disaster management. Elected by national NGO members to represent grassroots and community-based perspectives.',
    accent: 'from-emerald-400 to-emerald-600',
    cardHover: 'hover:border-emerald-500/30',
    iconWrap: 'bg-emerald-500/10 border-emerald-500/20',
    seatColor: 'text-emerald-400',
  },
  {
    icon: '🏢',
    seats: 1,
    type: 'Secretariat NGO Representative',
    desc: 'One seat for the NGO hosting the Secretariat - currently RAPID-Tanzania - ensuring operational continuity and administrative leadership within the Executive Committee.',
    accent: 'from-purple-400 to-purple-600',
    cardHover: 'hover:border-purple-500/30',
    iconWrap: 'bg-purple-500/10 border-purple-500/20',
    seatColor: 'text-purple-400',
  },
];

const MEETING_ROWS = [
  {
    dot: 'bg-[#00E5CC]',
    name: 'Partner Meetings',
    freq: 'Bimonthly',
    desc: 'All member organizations meet every two months for strategic updates, coordination, networking, and collective decision-making.',
  },
  {
    dot: 'bg-teal-400',
    name: 'Executive Committee Meetings',
    freq: 'As Required',
    desc: 'The Executive Committee convenes whenever specific operational or project needs arise - ensuring agile and responsive governance.',
  },
  {
    dot: 'bg-slate-500',
    name: 'Annual General Review',
    freq: 'Annually',
    desc: 'Full network review assessing annual progress, impact measurement, financial status, and strategic planning for the coming year.',
  },
];

const DECISION_STEPS = [
  {
    name: 'Consensus First',
    desc: 'All decisions are initially pursued through open dialogue and full member consensus - prioritizing unified agreement.',
  },
  {
    name: 'Majority Vote Fallback',
    desc: 'When consensus cannot be reached, a democratic majority vote among members determines the outcome, ensuring no deadlock.',
  },
  {
    name: 'Annual Review & Feedback',
    desc: 'Every decision and activity is subject to an annual review. Member feedback is integral to evaluating network progress and impact.',
  },
  {
    name: 'Transparent Reporting',
    desc: 'Annual reports summarizing all activities, key decisions, achievements, and financial status are shared with all members.',
  },
];

const FUNDING_CARDS = [
  {
    icon: '💳',
    title: 'Membership Fee',
    amount: 50000,
    amountPrefix: 'TZS ',
    label: 'One-Time Registration',
    desc: 'Paid once upon joining DINGONET as a new member organization. Covers administrative onboarding and network registration.',
  },
  {
    icon: '📆',
    title: 'Monthly Contribution',
    amount: 50000,
    amountPrefix: 'TZS ',
    label: 'Per Month / TZS 600,000 Per Year',
    desc: 'Regular monthly contribution from each member organization sustains coordination activities, meetings, and secretariat operations.',
  },
  {
    icon: '🤝',
    title: 'Grants & Donations',
    amount: 'Open',
    amountPrefix: '',
    label: 'External Funding Welcome',
    desc: 'DINGONET actively seeks grants, donations, and in-kind contributions from development partners and donors to fund joint programs and activities.',
  },
];

const COMMUNICATION_ITEMS = [
  {
    icon: '📧',
    title: 'Email & Newsletters',
    desc: 'Regular updates shared with all members through email and periodic newsletters.',
  },
  {
    icon: '📊',
    title: 'Infographics & Dashboards',
    desc: 'Visual data summaries shared via social media and member communication channels.',
  },
  {
    icon: '📝',
    title: 'Annual Reports',
    desc: 'Comprehensive annual summaries covering activities, achievements, impact, and financial status.',
  },
  {
    icon: '📱',
    title: 'Social Media Updates',
    desc: 'Real-time coordination updates and humanitarian information shared across social platforms.',
  },
];

const ME_CARDS = [
  {
    name: 'Progress Assessment',
    desc: 'Annual evaluation of all network activities against stated objectives and strategic goals.',
  },
  {
    name: 'Member Feedback Collection',
    desc: 'Structured feedback from all member organizations informs the review and drives continuous improvement.',
  },
  {
    name: 'Impact Reporting',
    desc: 'Findings published in the Annual Report and shared with members, donors, and government counterparts.',
  },
];

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [inView, threshold]);

  return [ref, inView];
};

const useCounter = (target, duration = 1800, shouldStart) => {
  const [count, setCount] = useState(typeof target === 'number' ? 0 : target);

  useEffect(() => {
    if (typeof target !== 'number') {
      setCount(target);
      return undefined;
    }

    if (!shouldStart) {
      setCount(0);
      return undefined;
    }

    let rafId = null;
    let start = null;

    const step = (timestamp) => {
      if (!start) {
        start = timestamp;
      }
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setCount(Math.round(target * eased));
      if (progress < 1) {
        rafId = window.requestAnimationFrame(step);
      }
    };

    rafId = window.requestAnimationFrame(step);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [target, duration, shouldStart]);

  return count;
};

const ExecutiveSeatCard = ({ seat, index, inView }) => {
  const count = useCounter(seat.seats, 1800, inView);

  return (
    <article
      style={{ transitionDelay: `${index * 100}ms` }}
      className={`overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 text-center transition-all duration-700 ${seat.cardHover} ${
        inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
    >
      <div className={`h-1 rounded-t-2xl bg-gradient-to-r ${seat.accent}`} />
      <div className="p-8">
        <div
          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border text-2xl ${seat.iconWrap}`}
        >
          {seat.icon}
        </div>
        <p className={`text-4xl font-black ${seat.seatColor}`}>
          {count} {seat.seats > 1 ? 'Seats' : 'Seat'}
        </p>
        <h3 className="mb-3 mt-1 text-base font-bold text-white">{seat.type}</h3>
        <p className="text-sm leading-relaxed text-slate-400">{seat.desc}</p>
      </div>
    </article>
  );
};

const FundingCard = ({ card, index, inView }) => {
  const amount = useCounter(card.amount, 1800, inView);

  return (
    <article
      style={{ transitionDelay: `${index * 120}ms` }}
      className={`rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 text-center transition-all duration-700 hover:border-[#00E5CC]/40 hover:shadow-[0_0_40px_rgba(0,229,204,0.12)] ${
        inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
    >
      <div className="mb-4 text-4xl">{card.icon}</div>
      <h3 className="mb-2 text-xl font-bold text-white">{card.title}</h3>
      <p className="text-3xl font-black text-[#00E5CC]">
        {card.amountPrefix}
        {typeof amount === 'number' ? amount.toLocaleString() : amount}
      </p>
      <p className="mt-1 text-xs uppercase tracking-wider text-slate-400">{card.label}</p>
      <p className="mt-4 text-sm leading-relaxed text-slate-400">{card.desc}</p>
    </article>
  );
};

const GovernancePage = () => {
  const [heroRef, heroInView] = useInView(0.1);
  const [principlesRef, principlesInView] = useInView(0.12);
  const [structureRef, structureInView] = useInView(0.12);
  const [seatsRef, seatsInView] = useInView(0.12);
  const [meetingsRef, meetingsInView] = useInView(0.12);
  const [fundingRef, fundingInView] = useInView(0.12);
  const [commsRef, commsInView] = useInView(0.12);
  const [meRef, meInView] = useInView(0.12);
  const [ctaRef, ctaInView] = useInView(0.15);

  return (
    <div className="min-h-screen bg-[#0B1629] text-white">
      <main>
        <section
          ref={heroRef}
          className="relative mx-auto max-w-4xl overflow-hidden px-6 pb-24 pt-36 text-center"
        >
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-1/2 h-[380px] w-[750px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00E5CC]/5 blur-3xl" />
            <div className="absolute right-16 top-20 h-60 w-60 rounded-full bg-teal-400/5 blur-2xl" />
            <div className="absolute bottom-10 left-10 h-48 w-48 rounded-full bg-[#00E5CC]/3 blur-2xl" />
          </div>
          <div
            className={`transition-all duration-700 ${
              heroInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
            }`}
          >
            <span className="mb-6 inline-block rounded-full border border-[#00E5CC]/30 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#00E5CC]">
              Governance
            </span>
            <h1 className="mb-6 text-5xl font-black leading-tight md:text-7xl">
              Transparent Leadership,
              <span className="block text-[#00E5CC]">Accountable Action</span>
            </h1>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-slate-400">
              DINGONET&apos;s governance structure is built on democratic representation, collective
              decision-making, and full transparency - ensuring every member organization has a
              voice in shaping Tanzania&apos;s humanitarian coordination.
            </p>
          </div>
        </section>

        <section ref={principlesRef} className="mx-auto max-w-6xl px-6 pb-20">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-black text-white md:text-4xl">
              Our <span className="text-[#00E5CC]">Core Values</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              The principles that guide how DINGONET members work together in pursuit of effective humanitarian coordination.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {PRINCIPLES.map((item, index) => (
              <article
                key={item.title}
                style={{ transitionDelay: `${index * 80}ms` }}
                className={`rounded-2xl border border-white/10 bg-white/5 p-6 text-center transition-all duration-700 hover:border-[#00E5CC]/30 ${
                  principlesInView ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                }`}
              >
                <div className="mb-3 text-3xl">{item.icon}</div>
                <h3 className="mb-2 text-base font-bold text-white">{item.title}</h3>
                <p className="text-xs leading-relaxed text-slate-400">{item.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section ref={structureRef} className="mx-auto max-w-5xl px-6 pb-28">
          <div
            className={`mb-16 text-center transition-all duration-700 ${
              structureInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
            }`}
          >
            <h2 className="text-3xl font-black text-white md:text-4xl">
              Our <span className="text-[#00E5CC]">Governance Structure</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              A clear, three-tier structure ensures strategic oversight, operational coordination,
              and day-to-day execution.
            </p>
          </div>

          <div className="flex flex-col items-center gap-0">
            <div
              className={`mx-auto w-full max-w-sm rounded-2xl border border-[#00E5CC]/40 bg-gradient-to-br from-[#00E5CC]/20 to-[#00BFA5]/10 px-12 py-6 text-center transition-all duration-700 ${
                structureInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
            >
              <div className="mb-2 text-3xl">🏛️</div>
              <h3 className="text-xl font-black text-white">General Assembly</h3>
              <p className="text-sm text-[#00E5CC]">All Member Organizations</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">
                Supreme decision-making body. Meets annually to review progress, elect leaders, and
                set strategic direction.
              </p>
            </div>

            <div className="mx-auto h-10 w-px bg-gradient-to-b from-[#00E5CC]/60 to-[#00E5CC]/20" />

            <div
              className={`mx-auto w-full max-w-md rounded-2xl border border-[#00E5CC]/25 bg-gradient-to-br from-slate-800/90 to-slate-900/90 px-10 py-6 text-center transition-all delay-100 duration-700 ${
                structureInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
            >
              <div className="mb-2 text-3xl">⭐</div>
              <h3 className="text-xl font-black text-white">Executive Committee</h3>
              <p className="text-sm text-[#00E5CC]">6 Elected Representatives</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">
                3 International NGOs - 2 National NGOs - 1 Secretariat NGO One-year tenure. Meets
                as required for operational decisions.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                <span className="rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs text-blue-400">
                  3 x International NGO
                </span>
                <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-400">
                  2 x National NGO
                </span>
                <span className="rounded-full border border-purple-500/20 bg-purple-500/10 px-3 py-1 text-xs text-purple-400">
                  1 x Secretariat NGO
                </span>
              </div>
            </div>

            <div className="mx-auto h-10 w-px bg-gradient-to-b from-[#00E5CC]/60 to-[#00E5CC]/20" />

            <div
              className={`mx-auto w-full max-w-lg rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 px-10 py-6 text-center transition-all delay-200 duration-700 ${
                structureInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
            >
              <div className="mb-2 text-3xl">🏢</div>
              <h3 className="text-xl font-black text-white">Secretariat</h3>
              <p className="text-sm text-[#00E5CC]">Hosted by RAPID-Tanzania - Dar es Salaam</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">
                Responsible for day-to-day coordination, administration, communication,
                documentation, and liaison with government authorities and partner organizations.
              </p>
            </div>
          </div>
        </section>

        <section ref={seatsRef} className="mx-auto max-w-5xl px-6 pb-28">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-black text-white md:text-4xl">
              Executive Committee <span className="text-[#00E5CC]">Composition</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              Six elected representatives govern DINGONET - ensuring balanced voices from across
              Tanzania&apos;s humanitarian community.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {EXEC_SEATS.map((seat, index) => (
              <ExecutiveSeatCard key={seat.type} seat={seat} index={index} inView={seatsInView} />
            ))}
          </div>
        </section>

        <section ref={meetingsRef} className="mx-auto max-w-6xl px-6 pb-28">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-black text-white md:text-4xl">
              How We <span className="text-[#00E5CC]">Meet & Decide</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              DINGONET&apos;s meeting structure ensures all members are informed, consulted, and
              empowered to shape collective decisions.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <article
              className={`rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 transition-all duration-700 ${
                meetingsInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
            >
              <h3 className="mb-6 text-lg font-bold text-white">📅 Meeting Schedule</h3>
              {MEETING_ROWS.map((row) => (
                <div
                  key={row.name}
                  className="flex items-start gap-4 border-b border-white/5 pb-5 last:border-0"
                >
                  <span className={`mt-1 h-3 w-3 flex-shrink-0 rounded-full ${row.dot}`} />
                  <div>
                    <p className="text-sm font-semibold text-white">{row.name}</p>
                    <p className="text-xs font-medium text-[#00E5CC]">{row.freq}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">{row.desc}</p>
                  </div>
                </div>
              ))}
            </article>

            <article
              className={`rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 transition-all delay-100 duration-700 ${
                meetingsInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
            >
              <h3 className="mb-6 text-lg font-bold text-white">⚖️ Decision-Making Process</h3>
              {DECISION_STEPS.map((step, index) => (
                <div
                  key={step.name}
                  className="flex items-start gap-4 border-b border-white/5 pb-5 last:border-0"
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 text-xs font-bold text-[#00E5CC]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">{step.name}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </article>
          </div>
        </section>

        <section ref={fundingRef} className="mx-auto max-w-5xl px-6 pb-28">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-black text-white md:text-4xl">
              Funding & <span className="text-[#00E5CC]">Resources</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              DINGONET sustains its operations through a transparent, member-supported funding model
              backed by grants and partnerships.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {FUNDING_CARDS.map((card, index) => (
              <FundingCard key={card.title} card={card} index={index} inView={fundingInView} />
            ))}
          </div>
          <div className="mx-auto mt-8 flex max-w-2xl items-center gap-3 rounded-xl border border-[#00E5CC]/15 bg-[#00E5CC]/5 px-6 py-4">
            <span className="text-xl text-[#00E5CC]">🔒</span>
            <p className="text-sm text-slate-300">
              A transparent financial management system governs all DINGONET funds. Annual
              financial reports are shared with all member organizations.
            </p>
          </div>
        </section>

        <section ref={commsRef} className="mx-auto max-w-6xl px-6 pb-28">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-black text-white md:text-4xl">
              Communication & <span className="text-[#00E5CC]">Accountability</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-400">
              We keep every member informed, every stakeholder engaged, and every action documented.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {COMMUNICATION_ITEMS.map((item, index) => (
              <article
                key={item.title}
                style={{ transitionDelay: `${index * 80}ms` }}
                className={`rounded-2xl border border-white/10 bg-white/5 p-6 text-center transition-all duration-700 hover:border-[#00E5CC]/30 hover:bg-[#00E5CC]/5 ${
                  commsInView ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                }`}
              >
                <div className="mb-3 text-4xl">{item.icon}</div>
                <h3 className="mb-2 text-sm font-semibold text-white">{item.title}</h3>
                <p className="text-xs leading-relaxed text-slate-400">{item.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section ref={meRef} className="mx-auto max-w-5xl px-6 pb-28">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div
              className={`transition-all duration-700 ${
                meInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
            >
              <span className="mb-4 inline-block rounded-full border border-[#00E5CC]/30 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#00E5CC]">
                M&E Framework
              </span>
              <h2 className="mb-4 text-3xl font-black text-white md:text-4xl">
                Measuring Our <span className="text-[#00E5CC]">Impact</span>
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-slate-400">
                DINGONET conducts an annual review to assess the network&apos;s progress, measure
                collective impact, and evaluate the effectiveness of coordinated humanitarian action
                across Tanzania.
              </p>
              <p className="text-sm leading-relaxed text-slate-400">
                Member feedback is central to this process - ensuring the network continuously adapts
                to the needs of the humanitarian community and the communities we serve.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <span className="rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 px-4 py-2 text-sm text-[#00E5CC]">
                  Annual Reviews
                </span>
                <span className="rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 px-4 py-2 text-sm text-[#00E5CC]">
                  Member Feedback Loops
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {ME_CARDS.map((card, index) => (
                <article
                  key={card.name}
                  style={{ transitionDelay: `${index * 100}ms` }}
                  className={`flex items-start gap-4 rounded-xl border border-white/10 bg-white/5 p-5 transition-all duration-700 ${
                    meInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
                  }`}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#00E5CC]/20 bg-[#00E5CC]/10 text-xs font-bold text-[#00E5CC]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">{card.name}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">{card.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section ref={ctaRef} className="mx-auto max-w-3xl px-6 pb-24 text-center">
          <div
            className={`rounded-3xl border border-[#00E5CC]/20 bg-gradient-to-br from-[#00E5CC]/10 to-[#00BFA5]/5 p-12 transition-all duration-700 ${
              ctaInView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
            }`}
          >
            <div className="mb-4 text-5xl">🏢</div>
            <h2 className="mb-4 text-3xl font-black text-white">Contact the Secretariat</h2>
            <p className="mx-auto mb-8 max-w-md text-slate-400">
              For governance inquiries, membership applications, meeting schedules, or partnership
              discussions - reach the DINGONET Secretariat hosted at RAPID-Tanzania.
            </p>
            <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
              <div className="rounded-xl border border-white/10 bg-white/5 px-6 py-4">
                <p className="text-xs uppercase tracking-wider text-slate-500">📧 Email</p>
                <p className="mt-1 text-sm font-semibold text-[#00E5CC]">info@dingonet.org</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-6 py-4 text-left">
                <p className="text-xs uppercase tracking-wider text-slate-500">📞 Contact</p>
                <p className="mt-2 text-sm font-semibold text-white">Laurent Wambura, Executive Director</p>
                <p className="text-sm font-semibold text-[#00E5CC]">0757724717</p>
                <p className="mt-2 text-sm font-semibold text-white">
                  Suhaila Stanthon, Lead of Advocacy and Communication
                </p>
                <p className="text-sm font-semibold text-[#00E5CC]">0744388179</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-6 py-4">
                <p className="text-xs uppercase tracking-wider text-slate-500">📍 Office</p>
                <p className="mt-1 text-sm font-semibold text-[#00E5CC]">Dar es Salaam, Tanzania</p>
              </div>
            </div>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <button
                type="button"
                className="rounded-full bg-[#00E5CC] px-8 py-3 font-bold text-[#0B1629] transition hover:bg-[#00BFA5]"
              >
                Get in Touch
              </button>
              <button
                type="button"
                className="rounded-full border border-white/20 px-8 py-3 text-white transition hover:border-white/40"
              >
                Download Terms of Reference
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        <p>
          DINGONET Secretariat (Hosted by RAPID-Tanzania) - P.O. Box 11189, Dar es Salaam -
          info@dingonet.org - Executive Director 0757724717 · Lead of Advocacy and Communication 0744388179
        </p>
        <p className="mt-2">Reg. No. 8894 - Registered January 2026</p>
      </footer>
    </div>
  );
};

export default GovernancePage;
